"use client"

import { useState, useEffect } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Checkbox } from "@/components/ui/checkbox"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { useToast } from "@/hooks/use-toast"
import { createClient } from "@/lib/supabase/client"
import { Waves, Calendar, Clock, Info, X, MapPin } from "lucide-react"

import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Star } from "lucide-react"

interface LogSessionModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSessionLogged?: () => void
  editingSession?: any // Optional session data for editing
}

interface SurfSpot {
  id: string
  name: string
  location: string
  country: string
}

export function LogSessionModal({
  open,
  onOpenChange,
  onSessionLogged,
  editingSession,
}: LogSessionModalProps) {
  const [surfSpots, setSurfSpots] = useState<SurfSpot[]>([])
  const [loadingSpots, setLoadingSpots] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState({
    id: "",
    surf_spot_id: "",
    session_date: new Date().toISOString().split("T")[0],
    session_start_time: "",
    session_end_time: "",
    is_all_day: false,
    duration_minutes: "",
    notes: "",
    activity_type: "surf",
    conditions_rating: 0,
  })

  useEffect(() => {
    if (editingSession) {
      setFormData({
        id: editingSession.id,
        surf_spot_id: editingSession.surf_spot_id || editingSession.surf_spots?.id || "",
        session_date: editingSession.session_date,
        session_start_time: editingSession.session_start_time ? editingSession.session_start_time.split("T")[1]?.slice(0, 5) : "",
        session_end_time: editingSession.session_end_time ? editingSession.session_end_time.split("T")[1]?.slice(0, 5) : "",
        is_all_day: editingSession.is_all_day || false,
        duration_minutes: editingSession.duration_minutes?.toString() || "",
        notes: editingSession.notes || "",
        activity_type: editingSession.activity_type || "surf",
        conditions_rating: editingSession.conditions_rating || 0,
      })
    } else if (open) {
      // Reset to defaults if not editing
      setFormData({
        id: "",
        surf_spot_id: "",
        session_date: new Date().toISOString().split("T")[0],
        session_start_time: "",
        session_end_time: "",
        is_all_day: false,
        duration_minutes: "",
        notes: "",
        activity_type: "surf",
        conditions_rating: 0,
      })
    }
  }, [editingSession, open])

  const { toast } = useToast()

  const isFutureDate = formData.session_date ? new Date(formData.session_date) > new Date() : false

  useEffect(() => {
    if (open) {
      fetchSurfSpots()
    }
  }, [open])

  const fetchSurfSpots = async () => {
    setLoadingSpots(true)
    try {
      const supabase = createClient()
      const { data, error } = await supabase
        .from("surf_spots")
        .select("id, name, location, country")
        .order("name", { ascending: true })

      if (error) {
        console.error("Error fetching surf spots:", error)
        toast({
          title: "Error",
          description: "Failed to load surf spots. Please try again.",
          variant: "destructive",
        })
        return
      }

      setSurfSpots(data || [])
    } catch (error) {
      console.error("Error fetching surf spots:", error)
      toast({
        title: "Error",
        description: "Failed to load surf spots. Please try again.",
        variant: "destructive",
      })
    } finally {
      setLoadingSpots(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.surf_spot_id || !formData.session_date) {
      toast({
        title: "Validation Error",
        description: "Please select a surf spot and enter a session date.",
        variant: "destructive",
      })
      return
    }

    // Validate times if not all-day
    if (!formData.is_all_day) {
      if (!formData.session_start_time || !formData.session_end_time) {
        toast({
          title: "Validation Error",
          description: "Please enter both start and end times, or check 'All Day'.",
          variant: "destructive",
        })
        return
      }

      // Validate end time is after start time
      const startDateTime = new Date(`${formData.session_date}T${formData.session_start_time}`)
      const endDateTime = new Date(`${formData.session_date}T${formData.session_end_time}`)
      
      if (endDateTime <= startDateTime) {
        toast({
          title: "Validation Error",
          description: "End time must be after start time.",
          variant: "destructive",
        })
        return
      }
    }

    setIsSubmitting(true)

    try {
      // Build request body
      const requestBody: any = {
        surf_spot_id: formData.surf_spot_id,
        session_date: formData.session_date,
        is_all_day: formData.is_all_day,
        notes: formData.notes.trim() || null,
        activity_type: formData.activity_type,
      }

      if (formData.conditions_rating > 0) {
        requestBody.conditions_rating = formData.conditions_rating
      }

      // Add times if not all-day
      if (!formData.is_all_day && formData.session_start_time && formData.session_end_time) {
        requestBody.session_start_time = `${formData.session_date}T${formData.session_start_time}`
        requestBody.session_end_time = `${formData.session_date}T${formData.session_end_time}`
      }

      // Duration is optional (will be calculated from times if provided)
      if (formData.duration_minutes) {
        requestBody.duration_minutes = parseInt(formData.duration_minutes)
      }

      const url = formData.id ? `/api/surf-sessions/${formData.id}` : "/api/surf-sessions"
      const method = formData.id ? "PATCH" : "POST"

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(requestBody),
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || "Failed to log session")
      }

      toast({
        title: "Success",
        description: formData.id
          ? "Your surf session has been updated successfully!"
          : isFutureDate 
            ? "Your surf session has been planned successfully!" 
            : "Your surf session has been logged successfully!",
      })

      // Reset form
      setFormData({
        surf_spot_id: "",
        session_date: new Date().toISOString().split("T")[0],
        session_start_time: "",
        session_end_time: "",
        is_all_day: false,
        duration_minutes: "",
        notes: "",
        activity_type: "surf",
        conditions_rating: 0,
        wave_height_ft: "",
      })

      // Close modal
      onOpenChange(false)

      // Call callback to refresh data
      if (onSessionLogged) {
        onSessionLogged()
      }
    } catch (error: any) {
      console.error("Error logging session:", error)
      toast({
        title: "Error",
        description: error.message || "Failed to log session. Please try again.",
        variant: "destructive",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[95vh] overflow-y-auto bg-white dark:bg-slate-900 border-none rounded-[2.5rem] shadow-2xl p-0 gap-0 z-[200] !top-24 sm:!top-[50%] !translate-y-0 sm:!translate-y-[-50%]">
        <div className="p-6 md:p-8 space-y-8 relative">
          <div className="flex items-start justify-between">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-blue-50 dark:bg-blue-900/20 rounded-2xl shadow-sm border border-blue-100/50 dark:border-blue-900/30">
                  <Waves className="w-6 h-6 text-blue-600" />
                </div>
                <DialogTitle className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight">
                  {formData.id 
                    ? (isFutureDate ? "Update Plan" : "Update Logged Session")
                    : (isFutureDate ? "Plan Surf Session" : "Log Surf Session")}
                </DialogTitle>
              </div>
              <DialogDescription className="text-[11px] font-medium text-slate-500 dark:text-slate-400 mt-1">
                {formData.id
                  ? "Update your session details"
                  : isFutureDate 
                    ? "Schedule your next time in the water" 
                    : "Record your time in the water to track your progress"}
              </DialogDescription>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Activity Type Selection */}
              <div className="space-y-3 col-span-full">
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Activity Type *</Label>
                <RadioGroup 
                  value={formData.activity_type} 
                  onValueChange={(v: any) => setFormData({ ...formData, activity_type: v })}
                >
                  <div className="grid grid-cols-2 gap-3">
                    <div
                      className={`flex items-center space-x-3 p-4 border rounded-2xl cursor-pointer transition-all active:scale-[0.98] ${
                        formData.activity_type === "surf"
                          ? "border-blue-500 bg-blue-50/50 dark:bg-blue-900/20"
                          : "border-slate-100 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-950/30 hover:bg-slate-50 dark:hover:bg-slate-900"
                      }`}
                      onClick={() => setFormData({ ...formData, activity_type: "surf" })}
                    >
                      <RadioGroupItem value="surf" id="surf" className="sr-only" />
                      <Label htmlFor="surf" className="font-bold cursor-pointer flex flex-col items-center gap-1 flex-1">
                        <span className="text-2xl">🏄</span>
                        <span className="text-[10px] uppercase tracking-wider">Surfing</span>
                      </Label>
                    </div>
                    <div
                      className={`flex items-center space-x-3 p-4 border rounded-2xl cursor-pointer transition-all active:scale-[0.98] ${
                        formData.activity_type === "kite"
                          ? "border-blue-500 bg-blue-50/50 dark:bg-blue-900/20"
                          : "border-slate-100 dark:border-slate-800 bg-slate-50/30 dark:bg-slate-950/30 hover:bg-slate-50 dark:hover:bg-slate-900"
                      }`}
                      onClick={() => setFormData({ ...formData, activity_type: "kite" })}
                    >
                      <RadioGroupItem value="kite" id="kite" className="sr-only" />
                      <Label htmlFor="kite" className="font-bold cursor-pointer flex flex-col items-center gap-1 flex-1">
                        <span className="text-2xl">🪁</span>
                        <span className="text-[10px] uppercase tracking-wider">Kitesurfing</span>
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
              </div>

              {/* Surf Spot Selection */}
              <div className="space-y-2 col-span-full">
                <Label htmlFor="surf_spot_id" className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">
                  Surf Spot *
                </Label>
                {loadingSpots ? (
                  <div className="h-12 flex items-center gap-2 px-4 rounded-2xl bg-slate-50 dark:bg-slate-950 text-sm text-muted-foreground border border-slate-100 dark:border-slate-800 animate-pulse">
                    <Waves className="w-4 h-4 animate-pulse text-blue-500" />
                    Loading surf spots...
                  </div>
                ) : (
                  <Select
                    value={formData.surf_spot_id}
                    onValueChange={(value) =>
                      setFormData({ ...formData, surf_spot_id: value })
                    }
                    required
                  >
                    <SelectTrigger id="surf_spot_id" className="h-12 rounded-2xl border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 shadow-sm focus:ring-blue-500/20">
                      <SelectValue placeholder="Select a surf spot" />
                    </SelectTrigger>
                    <SelectContent className="rounded-2xl border-slate-100 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-900 z-[300]">
                      {surfSpots.map((spot) => (
                        <SelectItem key={spot.id} value={spot.id} className="rounded-xl focus:bg-slate-50 dark:focus:bg-slate-800 !bg-white dark:!bg-slate-900">
                          <div className="flex flex-col">
                            <span className="font-bold">{spot.name}</span>
                            <span className="text-[10px] opacity-60 uppercase tracking-wider">{spot.location}, {spot.country}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Session Date */}
              <div className="space-y-2 col-span-full md:col-span-1">
                <Label htmlFor="session_date" className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1 flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" />
                  Session Date *
                </Label>
                <Input
                  id="session_date"
                  type="date"
                  value={formData.session_date}
                  onChange={(e) =>
                    setFormData({ ...formData, session_date: e.target.value })
                  }
                  className="h-12 rounded-2xl border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 shadow-sm focus:ring-blue-500/20"
                  required
                />
              </div>

              {/* All Day Toggle */}
              <div className="col-span-full md:col-span-1 flex items-end pb-1.5">
                <div className="bg-slate-50 dark:bg-slate-950 rounded-[2rem] p-4 border border-slate-100 dark:border-slate-800 shadow-sm flex items-center justify-between w-full h-12">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="is_all_day" className="text-[10px] font-black uppercase tracking-widest text-slate-500 cursor-pointer">
                      All Day Session
                    </Label>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-3.5 h-3.5 text-slate-400 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800 rounded-2xl p-4 shadow-xl z-[350]">
                        <p className="text-xs font-medium leading-relaxed">
                          Specific session times help us improve forecasts by comparing predicted vs actual conditions. 
                          All-day sessions are counted as 2 hours by default.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                  <Checkbox
                    id="is_all_day"
                    checked={formData.is_all_day}
                    onCheckedChange={(checked) =>
                      setFormData({
                        ...formData,
                        is_all_day: checked === true,
                        session_start_time: checked ? "" : formData.session_start_time,
                        session_end_time: checked ? "" : formData.session_end_time,
                      })
                    }
                    className="h-5 w-5 rounded-lg border-slate-200"
                  />
                </div>
              </div>

              {/* Start and End Times */}
              {!formData.is_all_day && (
                <>
                  <div className="space-y-2 col-span-1">
                    <Label htmlFor="session_start_time" className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1 flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" />
                      Start Time *
                    </Label>
                    <Input
                      id="session_start_time"
                      type="time"
                      value={formData.session_start_time}
                      onChange={(e) =>
                        setFormData({ ...formData, session_start_time: e.target.value })
                      }
                      className="h-12 rounded-2xl border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 shadow-sm focus:ring-blue-500/20"
                      required={!formData.is_all_day}
                    />
                  </div>
                  <div className="space-y-2 col-span-1">
                    <Label htmlFor="session_end_time" className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1 flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" />
                      End Time *
                    </Label>
                    <Input
                      id="session_end_time"
                      type="time"
                      value={formData.session_end_time}
                      onChange={(e) =>
                        setFormData({ ...formData, session_end_time: e.target.value })
                      }
                      className="h-12 rounded-2xl border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 shadow-sm focus:ring-blue-500/20"
                      required={!formData.is_all_day}
                    />
                  </div>
                </>
              )}

              {/* Duration */}
              <div className="space-y-2 col-span-full md:col-span-1">
                <Label htmlFor="duration_minutes" className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1 flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" />
                  Duration (min)
                </Label>
                <Input
                  id="duration_minutes"
                  type="number"
                  min="1"
                  placeholder="e.g., 120"
                  value={formData.duration_minutes}
                  onChange={(e) =>
                    setFormData({ ...formData, duration_minutes: e.target.value })
                  }
                  className="h-12 rounded-2xl border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 shadow-sm focus:ring-blue-500/20"
                  disabled={!formData.is_all_day && formData.session_start_time !== "" && formData.session_end_time !== ""}
                />
              </div>

              {/* Conditions Rating (Hidden for future sessions) */}
              {!isFutureDate && (
                <div className="space-y-2 col-span-full">
                  <Label className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Conditions Rating</Label>
                  <div className="flex gap-2 bg-slate-50 dark:bg-slate-950 rounded-2xl p-3 border border-slate-100 dark:border-slate-800 shadow-sm w-fit">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setFormData({ ...formData, conditions_rating: star })}
                        className="p-1 transition-transform active:scale-90"
                      >
                        <Star 
                          className={`w-6 h-6 ${
                            star <= formData.conditions_rating 
                              ? "text-amber-400 fill-current" 
                              : "text-slate-200 dark:text-slate-800"
                          }`} 
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              <div className="space-y-2 col-span-full">
                <Label htmlFor="notes" className="text-[10px] font-black uppercase tracking-widest text-slate-400 ml-1">Notes</Label>
                <Textarea
                  id="notes"
                  placeholder="Share details about your session..."
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                  className="rounded-2xl border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 focus:ring-blue-500/20 min-h-[120px] py-4 shadow-sm"
                  rows={4}
                />
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex flex-col gap-3 pt-4 pb-8">
              <Button 
                type="submit" 
                disabled={isSubmitting || loadingSpots}
                className="h-14 bg-blue-600 hover:bg-blue-500 text-white rounded-2xl font-black uppercase tracking-widest shadow-lg shadow-blue-500/20 transition-all active:scale-95 border-none"
              >
                {isSubmitting 
                  ? (formData.id ? "Updating..." : (isFutureDate ? "Planning Session..." : "Logging Session...")) 
                  : (formData.id ? "Update Session" : (isFutureDate ? "Plan Session" : "Log Session"))}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
                className="h-12 text-slate-400 font-bold uppercase tracking-widest text-[10px] hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  )
}

